import { Add, Close, Search } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Badge, Card, ListGroup } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
import { useNotification } from '../../hooks/NotificationContext';
import { getTestCycleKeyFromLocation } from '../../lib/executionUtils';
import { generateNotificationUrl } from '../../lib/notificationUtils';
import { isExecutiveManager } from '../../lib/userUtils';
import { getPriorityColor } from '../../lib/utils';
import {
  associateIssuesBulk,
  fetchAssociatedIssues,
  removeAssociatedIssue,
} from '../../modules/associations';
import { getCurrentProject } from '../../modules/project';
import { Stack, Typography } from '@mui/material';
import IssueSuggestions from '../../components/project-dashboard/test-execution/test-execution-body/IssueSuggestions';
import ConfirmationModal from '../../ui/primitives/ConfirmationModel';
import CustomIconButton from '../../components/elements/CustomIconButton';
import SkeletonLoader from '../../components/elements/SkeletonLoader';
import { IssueTypeIcon } from '../../components/elements/IssueTypeIcon';
import SearchIssuesModalComponent from '../../components/elements/common/traceability/SearchIssuesModal';
import CreateJiraIssueForm from '../../components/elements/common/traceability/CreateIssueForm';
import QUERY_KEYS from '../../query/queryKeys';
import { WarningIcon } from '../../components/elements/CustomIcons';
import { RefreshButton } from '../../components/elements/CustomButtons';
import { CountChip } from '../../ui/primitives/Chips';
import DetailsCard from '../../ui/layout/DetailsCard';

const LinkedIssue = ({
  entityId,
  entityType,
  isShowSuggestion,
  data,
  entityKey,
  showWarning = false,
}) => {
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [issueKey, setIssueKey] = useState([]);
  const [step, setStep] = useState(1);
  const [selectedProjectFromSearch, setSelectedProjectFromSearch] = useState(null);
  const [submittingAdd, setSubmittingAdd] = useState(false);
  const { addNotification } = useNotification();
  const { projectId } = useParams();
  const location = useLocation();
  const testCycleKey = getTestCycleKeyFromLocation(location);
  const role = JSON.parse(localStorage.getItem('role'));
  const queryClient = useQueryClient();
  const forceRefreshRef = useRef(false);

  const { data: currentProject, isLoading: dataLoading } = useQuery({
    queryKey: QUERY_KEYS.project.current(projectId),
    queryFn: () => getCurrentProject(projectId),
  });

  const linkedJiraProjects = currentProject?.linked_jira_projects || [];

  const {
    data: linkedIssues = [],
    isLoading,
    error,
    isFetching,
    refetch: refetchLinkedIssues,
  } = useQuery({
    queryKey: [QUERY_KEYS.issues.list, entityId, projectId],
    queryFn: async () => {
      const currentForceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      return fetchAssociatedIssues(entityId, projectId, currentForceRefresh);
    },
    enabled: !!entityId && !!currentProject && linkedJiraProjects.length > 0,
    select: (res) =>
      res.issues?.map((issue) => ({
        key: issue.key,
        id: issue.jira_issue_id,
        summary: issue.summary,
        status: issue.status,
        issue_type: issue.issue_type,
        priority: issue.priority,
        jiraProjectName: issue.jira_project_name,
      })) || [],
  });

  const linkedIssueIds = linkedIssues.map((i) => i.id);

  const handleShowConfirm = (issue) => {
    setSelectedIssue(issue);
    setShowConfirm(true);
  };

  const handleConfirmRemove = async () => {
    try {
      await removeAssociatedIssue(entityId, selectedIssue.id);
      addNotification('success', 'Issue removed successfully.');
      refetchLinkedIssues();
    } catch {
      addNotification('danger', 'Failed to delete linked issue.');
    } finally {
      setShowConfirm(false);
      setSelectedIssue(null);
    }
  };

  const handleAddIssues = async (issues) => {
    const otherData =
      entityType === 'Execution'
        ? { testCaseKey: data?.key, testCycleKey: testCycleKey }
        : null;
    const entityUrl = generateNotificationUrl(
      entityType,
      projectId,
      entityKey,
      'traceability',
      otherData
    );
    const associations = {
      issues: issues.map((issue) => ({
        id: issue.id,
        key: issue.key,
        priority: issue.priority,
        projectId: issue.projectId,
      })),
      entityId,
      entityType,
      gainssProjectId: projectId,
      entityUrl,
      entityKey,
    };
    setSubmittingAdd(true);
    try {
      await associateIssuesBulk(associations, projectId);
      addNotification('success', 'Issue linked successfully.');
      handleRefresh();
      if (entityType === 'Execution') {
        setIssueKey(issues?.map((issue) => issue.key));
        setStep(2);
      } else {
        setShowSearchModal(false);
      }
    } catch (error) {
      addNotification('danger', error.message || 'Failed to create the Jira issue.');
    } finally {
      setSubmittingAdd(false);
    }
  };

  const handleRefresh = () => {
    forceRefreshRef.current = true;
    queryClient.refetchQueries({
      queryKey: [QUERY_KEYS.issues.list, entityId, projectId],
      exact: true,
    });
  };

  const handleShowCreateModal = () => {
    setShowCreateModal(true);
    setShowSearchModal(false);
  };

  const cardTitle = (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Typography variant="h6">Linked Issues</Typography>
      {linkedIssues?.length > 0 && (
        <CountChip label={linkedIssues?.length} />
      )}
      {!isLoading && showWarning && linkedIssues?.length === 0 && (
        <WarningIcon tooltipText="Link defects to document this failed execution." />
      )}
    </Stack>
  );

  const cardActions = (
    <div>
      {!isLoading && (
        <RefreshButton
          onRefresh={handleRefresh}
          isFetching={isFetching}
          showLastUpdated={false}
          buttonSize={20}
          noOutline={true}
        />
      )}
      {!isExecutiveManager() &&
        currentProject?.integrated_platform &&
        linkedJiraProjects?.length > 0 && (
          <>
            <CustomIconButton
              disableRipple
              onClick={() => setShowCreateModal(true)}
              title="Create New Issue">
              <Add color="primary" />
            </CustomIconButton>
            <CustomIconButton
              disableRipple
              onClick={() => setShowSearchModal(true)}
              title="Search & Link issues">
              <Search color="primary" />
            </CustomIconButton>
          </>
        )}
    </div>
  );

  return (
    <>
      <DetailsCard title={cardTitle} actions={cardActions}>
        {dataLoading ? (
          <SkeletonLoader rowCount={2} />
        ) : !currentProject?.integrated_platform ? (
          <Alert severity="warning">
            {role === 'org-admin' || role === 'org-manager'
              ? 'No project management tool is integrated. Please set up the integration to enable issue creation and linking.'
              : 'No project management tool is integrated. Please contact your organization admin to configure the integration.'}
          </Alert>
        ) : linkedJiraProjects.length === 0 ? (
          <Alert severity="warning">
            {role === 'org-admin' || role === 'org-manager'
              ? 'This project is not linked to any external project. Please link the project to enable issue creation and linking.'
              : 'This project is not linked to any external project. Please contact your organization admin to link it.'}
          </Alert>
        ) : (
          <Card.Body>
            {error && <p className="text-danger">{error.message}</p>}
            {isLoading ? (
              <SkeletonLoader />
            ) : linkedIssues?.length === 0 ? (
              <p className="text-center text-muted">No linked issues found.</p>
            ) : (
              <ListGroup as="ol">
                {linkedIssues?.map((issue) => (
                  <ListGroup.Item
                    key={issue.id}
                    as="li"
                    action
                    className="align-items-center"
                    onMouseOver={() => setSelectedIssue(issue)}
                    onMouseOut={() => !showConfirm && setSelectedIssue(null)}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}>
                    <div className="d-flex align-items-center flex-grow-1">
                      <span className="me-3" title={issue.issue_type}>
                        <IssueTypeIcon issueType={issue.issue_type} />
                      </span>
                      <a
                        href={`${currentProject?.jira_site_url}/browse/${issue.key}`}
                        target="_blank"
                        title="Open issue in new tab"
                        rel="noopener noreferrer"
                        className="fw-bold me-3 text-decoration-none">
                        {issue.key}
                      </a>
                      <div className="flex-grow-1">
                        <p className="m-0" title={issue.summary}>
                          {issue.summary?.length > 100
                            ? `${issue.summary.slice(0, 100)}...`
                            : issue.summary}
                        </p>
                      </div>
                    </div>
                    <div className="text-muted" style={{ fontSize: '14px' }}>
                      {issue?.jiraProjectName || ''}
                    </div>
                    <span className="d-flex align-items-center mx-4 px-2">
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          backgroundColor:
                            getPriorityColor(issue.priority) || '#ccc',
                          borderRadius: 2,
                          marginRight: 6,
                        }}
                      />
                      {issue.priority}
                    </span>
                    <div className="badge bg-light text-dark px-2 py-1 rounded text-uppercase">
                      {issue.status}
                    </div>
                    <div className="ms-3">
                      {!isExecutiveManager() &&
                        selectedIssue?.id === issue.id && (
                          <Badge
                            bg="danger"
                            pill
                            title="Remove Linked Issue"
                            onClick={() => handleShowConfirm(issue)}
                            style={{ cursor: 'pointer' }}>
                            <Close fontSize="sm" />
                          </Badge>
                        )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
            {isShowSuggestion && (
              <IssueSuggestions
                entityId={entityId}
                handleAddIssues={handleAddIssues}
                linkedIssueIds={linkedIssueIds}
                linkedJiraProjects={linkedJiraProjects}
                currentProject={currentProject}
              />
            )}
          </Card.Body>
        )}
      </DetailsCard >
      <SearchIssuesModalComponent
        show={showSearchModal}
        setShowSearchModal={setShowSearchModal}
        handleAddIssues={handleAddIssues}
        linkedIssueIds={linkedIssueIds}
        currentProject={currentProject}
        step={step}
        issueKey={issueKey}
        testCaseData={data}
        setStep={setStep}
        entityKey={entityKey}
        submitting={submittingAdd}
        setSubmitting={setSubmittingAdd}
        linkedJiraProjects={linkedJiraProjects}
        handleShowCreateModal={handleShowCreateModal}
        setSelectedProjectFromSearch={setSelectedProjectFromSearch}
      />
      <CreateJiraIssueForm
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        fetchLinkedIssues={handleRefresh}
        entityId={entityId}
        entityType={entityType}
        currentProject={currentProject}
        testCaseData={data}
        entityKey={entityKey}
        linkedJiraProjects={linkedJiraProjects}
        selectedProjectFromSearch={selectedProjectFromSearch}
      />
      <ConfirmationModal
        show={showConfirm}
        handleConfirm={handleConfirmRemove}
        handleClose={() => setShowConfirm(false)}
        title="Remove Linked Issue"
        body="Are you sure you want to remove this linked issue?"
      />
    </>
  );
};

export default LinkedIssue;
